<script setup lang="ts">
import { CalendarDays, CircleHelp, LogOut, QrCode } from 'lucide-vue-next';
import { RouterLink } from 'vue-router';
import UiAppHeader from '@krafta/ui-kit/components/UiAppHeader.vue';
import UiHamburgerButton from '@krafta/ui-kit/components/UiHamburgerButton.vue';
import UiIconButton from '@krafta/ui-kit/components/UiIconButton.vue';
import UiThemeToggle from '@krafta/ui-kit/components/UiThemeToggle.vue';
import UiButton from '@krafta/ui-kit/components/UiButton.vue';

withDefaults(defineProps<{
  version: string;
  isLight: boolean;
  themeLabel: string;
  themeClasses: { header: string };
  showScannerShortcut: boolean;
  showAttendanceEndShiftShortcut?: boolean;
  navigationLocked?: boolean;
  userInitials?: string;
  userName?: string;
  isMobileMenuOpen: boolean;
  toggleTheme: () => void;
  toggleMobileMenu: () => void;
  logout?: () => void;
}>(), {
  userInitials: undefined,
  userName: undefined,
  navigationLocked: false,
  showAttendanceEndShiftShortcut: false,
  logout: undefined
});

const emit = defineEmits<{
  openHelp: [];
}>();
</script>

<template>
  <UiAppHeader
    brand-title="Krafta"
    :brand-version="`версия ${version}`"
    brand-to="/"
    brand-aria-label="Krafta"
    :bordered="false"
    :class="themeClasses.header"
  >
    <template #controls>
      <UiIconButton
        v-if="!navigationLocked"
        :as="RouterLink"
        :to="{ name: 'calendar' }"
        data-help-id="app-calendar-button"
        variant="secondary"
        size="md"
        aria-label="Открыть календарь событий"
        title="Календарь"
      >
        <CalendarDays
          :size="18"
          :stroke-width="1.9"
          aria-hidden="true"
        />
      </UiIconButton>

      <UiIconButton
        v-if="!navigationLocked"
        data-help-id="app-help-button"
        type="button"
        variant="secondary"
        size="md"
        aria-label="Открыть помощь по экрану"
        title="Помощь"
        @click="emit('openHelp')"
      >
        <CircleHelp
          :size="18"
          :stroke-width="1.9"
          aria-hidden="true"
        />
      </UiIconButton>

      <UiIconButton
        v-if="showScannerShortcut"
        :as="RouterLink"
        :to="{ name: 'operator-scanner' }"
        class="app-header__scanner-link"
        variant="secondary"
        size="md"
        aria-label="Открыть сканер кодов"
      >
        <QrCode
          :size="18"
          :stroke-width="1.9"
          aria-hidden="true"
        />
      </UiIconButton>

      <UiButton
        v-if="showAttendanceEndShiftShortcut"
        :as="RouterLink"
        :to="{ name: 'attendance-scan' }"
        data-help-id="app-attendance-end-shift"
        variant="danger"
        size="md"
        class="app-header__shift-link"
      >
        Закончить смену
      </UiButton>

      <UiThemeToggle
        :is-light="isLight"
        :theme-label="themeLabel"
        @toggle="toggleTheme"
      />

      <details
        v-if="userInitials && logout"
        class="app-header__user-menu"
      >
        <summary
          class="app-header__avatar"
          :aria-label="`Профиль ${userName || userInitials}`"
        >
          <span>{{ userInitials }}</span>
        </summary>
        <div class="app-header__user-panel">
          <p class="app-header__user-name">
            {{ userName || 'Пользователь' }}
          </p>
          <UiButton
            unstyled
            class="app-header__logout"
            type="button"
            @click="logout?.()"
          >
            <LogOut :size="16" />
            <span>Выйти</span>
          </UiButton>
        </div>
      </details>

      <UiHamburgerButton
        v-if="!navigationLocked"
        :is-light="isLight"
        :is-open="isMobileMenuOpen"
        label="Меню"
        @toggle="toggleMobileMenu"
      />
    </template>
  </UiAppHeader>
</template>

<style scoped>
.app-header__scanner-link {
  flex: 0 0 auto;
}

.app-header__shift-link {
  white-space: nowrap;
}

.app-header__user-menu {
  position: relative;
}

.app-header__avatar {
  display: inline-flex;
  width: 2.5rem;
  height: 2.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: color-mix(in srgb, var(--ui-accent) 12%, var(--ui-surface));
  color: var(--ui-accent);
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.25rem;
  list-style: none;
  outline: none;
  transition:
    background-color 0.16s ease,
    color 0.16s ease,
    transform 0.16s ease,
    box-shadow 0.16s ease;
}

.app-header__avatar::-webkit-details-marker {
  display: none;
}

.app-header__avatar:hover {
  box-shadow: 0 8px 18px color-mix(in srgb, var(--ui-accent) 14%, transparent);
  transform: translateY(-1px);
}

.app-header__avatar:focus-visible {
  box-shadow: var(--ui-focus-ring), 0 8px 18px color-mix(in srgb, var(--ui-accent) 14%, transparent);
}

.app-header__user-menu[open] .app-header__avatar {
  background: var(--ui-accent);
  color: var(--ui-on-accent);
}

.app-header__user-panel {
  position: absolute;
  top: calc(100% + 0.75rem);
  right: 0;
  z-index: 50;
  width: 13.5rem;
  overflow: hidden;
  border: 1px solid var(--ui-border);
  border-radius: 0.875rem;
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-popover);
  padding: 0.75rem;
  transform-origin: top right;
  animation: app-header-panel-in 0.14s ease-out;
}

.app-header__user-name {
  margin: 0 0 0.5rem;
  color: var(--ui-foreground);
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
}

.app-header__logout {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.5rem;
  border: 0;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--ui-danger);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.625rem;
  text-align: left;
}

.app-header__logout:hover {
  background: color-mix(in srgb, var(--ui-danger) 9%, transparent);
}

.app-header__logout:focus-visible {
  outline: 2px solid var(--ui-control-ring);
  outline-offset: 2px;
}

@keyframes app-header-panel-in {
  from {
    opacity: 0;
    transform: translateY(-0.25rem) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 640px) {
  .app-header__shift-link {
    max-width: 8.75rem;
  }

  .app-header__user-panel {
    width: min(13.5rem, calc(100vw - 1.5rem));
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-header__avatar,
  .app-header__user-panel {
    animation: none;
    transition: none;
  }
}
</style>
